import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Flame, Sparkles, BookHeart, ArrowRight, Waves } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const MODES = [
    {
        key: "untangle",
        title: "Untangle",
        subtitle: "Fact from story, from body, from pattern, from desire.",
        Icon: Flame,
    },
    {
        key: "prepare",
        title: "Prepare",
        subtitle: "Build the clean ask, boundary, repair, or hard conversation.",
        Icon: Sparkles,
    },
    {
        key: "stay_with_me",
        title: "Stay with me",
        subtitle: "Lower strategy. More grounding and witnessed presence.",
        Icon: BookHeart,
    },
];

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const ctx = useOutletContext();
    const [starting, setStarting] = useState(false);
    const [todayIntention, setTodayIntention] = useState(null);
    const [recentMood, setRecentMood] = useState(null);

    useEffect(() => {
        api.get("/intentions/today").then((r) => setTodayIntention(r.data)).catch(() => {});
        api.get("/mood-logs?days=1").then((r) => setRecentMood(r.data?.[0] || null)).catch(() => {});
    }, []);

    const startMode = async (mode) => {
        if (starting) return;
        setStarting(true);
        try {
            const res = await api.post("/conversations", { mode });
            ctx?.refreshConversations?.();
            navigate(`/chat/${res.data.conversation_id}`);
        } catch (e) {
            toast.error("Could not open the space.");
        } finally {
            setStarting(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto w-full px-6 md:px-10 lg:px-14 py-10 md:py-14" data-testid="dashboard-page">
            {/* Greeting */}
            <div className="mb-10 md:mb-14">
                <div className="flex items-center gap-2 text-brand-charcoal/60 text-xs uppercase tracking-widest">
                    <Flame className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                    Today
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-darkPurple leading-[1.05] tracking-tight">
                    Hi, {user?.name?.split(" ")[0] || "there"}.
                    <br />
                    <em className="italic font-normal text-brand-darkPurple/80">What wants space to breathe?</em>
                </h1>

                <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                        data-testid="open-checkin-btn"
                        onClick={() => ctx?.onCheckIn?.()}
                        variant="outline"
                        className="rounded-full border-brand-darkPurple/20 bg-white hover:bg-brand-cream text-brand-darkPurple"
                    >
                        <Sparkles className="w-4 h-4 mr-2 text-brand-spark" strokeWidth={1.75} />
                        {recentMood ? "Update today's check-in" : "Daily check-in"}
                    </Button>
                    {todayIntention && (
                        <div className="rounded-full border border-brand-darkPurple/12 bg-white/70 px-4 py-2 text-sm text-brand-darkPurple">
                            <span className="text-brand-charcoal/55 mr-1.5 text-xs uppercase tracking-widest">Intention:</span>
                            {todayIntention.text}
                        </div>
                    )}
                </div>
            </div>

            {/* Three-mode cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {MODES.map(({ key, title, subtitle, Icon }, i) => (
                    <button
                        key={key}
                        data-testid={`mode-card-${key}`}
                        onClick={() => startMode(key)}
                        disabled={starting}
                        style={{ animationDelay: `${i * 80}ms` }}
                        className="group text-left rounded-2xl bg-white border border-brand-darkPurple/10 shadow-soft-card p-8 md:p-9 hover:shadow-hover-card hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 ease-out animate-fade-in-up"
                    >
                        <Icon className="w-6 h-6 text-brand-spark mb-6" strokeWidth={1.5} />
                        <h3 className="font-serif text-3xl text-brand-darkPurple tracking-tight">{title}</h3>
                        <p className="mt-3 text-brand-charcoal/75 leading-relaxed text-[15px]">{subtitle}</p>
                        <div className="mt-8 flex items-center gap-2 text-sm text-brand-darkPurple/80 group-hover:text-brand-spark transition-colors">
                            Begin here <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                    </button>
                ))}
            </div>

            {/* Secondary — patterns / insights entry */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
                <button
                    data-testid="dashboard-patterns-card"
                    onClick={() => navigate("/patterns")}
                    className="text-left rounded-2xl bg-brand-creamDeep/60 border border-brand-darkPurple/10 p-7 hover:bg-brand-creamDeep transition-colors"
                >
                    <Waves className="w-5 h-5 text-brand-spark mb-3" strokeWidth={1.5} />
                    <div className="font-serif text-2xl text-brand-darkPurple">Patterns</div>
                    <div className="text-sm text-brand-charcoal/70 mt-1">See what keeps circling back.</div>
                </button>
                <button
                    data-testid="dashboard-insights-card"
                    onClick={() => navigate("/insights")}
                    className="text-left rounded-2xl bg-brand-creamDeep/60 border border-brand-darkPurple/10 p-7 hover:bg-brand-creamDeep transition-colors"
                >
                    <BookHeart className="w-5 h-5 text-brand-spark mb-3" strokeWidth={1.5} />
                    <div className="font-serif text-2xl text-brand-darkPurple">Wisdom vault</div>
                    <div className="text-sm text-brand-charcoal/70 mt-1">The lines you saved. Return here often.</div>
                </button>
            </div>
        </div>
    );
}
